import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getServiceUnitPrice } from '@/util/service-pricing';
import { formatPriceDisplay } from '@/util/formatting';
import { ServiceType } from './carousel.types';

// Pixel slack absorbed when checking scroll-edge state, accounting for sub-pixel rounding.
const SCROLL_EDGE_THRESHOLD_PX = 2;

interface BadgeCarouselProps {
  services: ServiceType[];
  selectedServiceId: string | null;
  onSelect: (serviceId: string) => void;
  cartServiceSlugs?: Set<string>;
}

export default function BadgeCarousel({
  services,
  selectedServiceId,
  onSelect,
  cartServiceSlugs
}: BadgeCarouselProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [showControls, setShowControls] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    if (viewportRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = viewportRef.current;
      setCanScrollLeft(scrollLeft > SCROLL_EDGE_THRESHOLD_PX);
      setCanScrollRight(
        scrollLeft < scrollWidth - clientWidth - SCROLL_EDGE_THRESHOLD_PX
      );
    }
  }, []);

  useEffect(() => {
    const checkScrollable = () => {
      if (viewportRef.current) {
        const { scrollWidth, clientWidth } = viewportRef.current;
        setShowControls(scrollWidth > clientWidth);
        updateScrollState();
      }
    };
    checkScrollable();
    const observer = new ResizeObserver(checkScrollable);
    if (viewportRef.current) observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [services, updateScrollState]);

  const handleViewportScroll = useCallback(() => {
    updateScrollState();
  }, [updateScrollState]);

  useEffect(() => {
    if (selectedServiceId) {
      const activeItem = itemRefs.current[selectedServiceId];
      if (activeItem && viewportRef.current) {
        activeItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [selectedServiceId]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (viewportRef.current) {
      const scrollAmount = viewportRef.current.clientWidth * 0.5;
      viewportRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const sortedServices = useMemo(
    () =>
      [...services].sort(
        (a, b) => (a.onlineSortOrder ?? 0) - (b.onlineSortOrder ?? 0),
      ),
    [services],
  );

  if (!services || services.length === 0) return null;

  return (
    <div
      className={cn(
        'absolute left-3 right-3 bottom-3 z-[35]',
        'flex items-center gap-2 h-10'
      )}
    >
      {showControls && (
        <button
          className={cn(
            'w-8 h-10 rounded-full border-none bg-white',
            'flex items-center justify-center cursor-pointer',
            'shrink-0 transition-all duration-200',
            'hover:bg-neutral-100 disabled:cursor-default disabled:opacity-50',
            'disabled:hover:bg-white'
          )}
          onClick={() => handleScroll('left')}
          disabled={!canScrollLeft}
          aria-label="Scroll Services Left"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}

      <div
        className={cn(
          'flex-1 overflow-x-auto flex items-center',
          'gap-2 h-full scroll-smooth',
          'scrollbar-none'
        )}
        ref={viewportRef}
        onScroll={handleViewportScroll}
      >
        {sortedServices.map((s) => {
          const isActive = selectedServiceId === s.uuid;
          const isInCart = cartServiceSlugs?.has(s.shoppingCartSlug) ?? false;
          const price = getServiceUnitPrice(s);
          const highlighted = isActive || isInCart;

          return (
            <button
              key={s.uuid}
              ref={(el) => { itemRefs.current[s.uuid] = el; }}
              className={cn(
                'cursor-pointer flex items-center justify-between',
                'px-3 py-2 rounded-full font-semibold text-white text-sm',
                'border border-neutral-400 shrink-0 w-fit',
                'transition-all duration-200 shadow-[0_4px_10px_#00000014]',
                highlighted
                  ? 'bg-primary/80 border-primary/25'
                  : 'bg-black/50 hover:bg-black/70'
              )}
              onClick={() => onSelect(s.uuid)}
            >
              <span className="flex items-center gap-1.5">
                {highlighted ? (
                  <Minus className="h-[18px] w-[18px] text-white drop-shadow-[1px_1px_3px_var(--color-shadow-text-on-image)]" />
                ) : (
                  <Plus className="h-[18px] w-[18px] text-white drop-shadow-[1px_1px_3px_var(--color-shadow-text-on-image)]" />
                )}
                <span
                  className={cn(
                    'overflow-hidden text-ellipsis whitespace-nowrap',
                    'font-medium',
                    '[text-shadow:1px_1px_3px_var(--color-shadow-text-on-image)]'
                  )}
                >
                  {s.name}
                </span>
              </span>
              <span
                className={cn(
                  'ml-3 text-white font-semibold text-[10px]',
                  highlighted &&
                    '[text-shadow:1px_1px_3px_var(--color-shadow-text-on-image)]'
                )}
              >
                ${formatPriceDisplay(price)}
              </span>
            </button>
          );
        })}
      </div>

      {showControls && (
        <button
          className={cn(
            'w-8 h-10 rounded-full border-none bg-white',
            'flex items-center justify-center cursor-pointer',
            'shrink-0 transition-all duration-200',
            'hover:bg-neutral-100 disabled:cursor-default disabled:opacity-50',
            'disabled:hover:bg-white'
          )}
          onClick={() => handleScroll('right')}
          disabled={!canScrollRight}
          aria-label="Scroll Services Right"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
